import React, {useEffect, useRef, useState} from "react";
import {useAppDispatch, useNavigateByName, useProfileData} from "../../../hooks";
import {createAuthTokenManager, createBorrowingOnboardingManager, createProfileManager} from "../../../di";
import {RouteDictionary} from "../../../utils";

import {set, setBorrowerOnboardingComplete} from "../../../store/profile";
import {OnboardingStep, Role} from "../../../api-client";
import {Button, PrimaryButton} from "../../../components/common";
import {PrimaryButtonColor} from "../../../components/common/buttons/decorators/PrimaryButton/PrimaryButton";

const PROFILE_RETRY_DELAYS = [1000, 2000, 4000, 8000, 8000];

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const RedirectAfterAuth = () => {
  const navigate = useNavigateByName();
  const profileData = useProfileData();
  const dispatch = useAppDispatch();
  const [failed, setFailed] = useState(false);
  const cancelledRef = useRef(false);

  const redirectByProfile = async (profile: {role: Role; isVerified: boolean}) => {
    if (!profile.isVerified) {
      if (profile.role === Role.BORROWER) {
        navigate(RouteDictionary.IDENTIFICATION_BORROWER);
      } else if (profile.role === Role.LENDER) {
        navigate(RouteDictionary.IDENTIFICATION);
      }
      return;
    }

    if (profile.role === Role.BORROWER) {
      try {
        const manager = await createBorrowingOnboardingManager();
        let state;
        try {
          state = await manager.get();
        } catch {
          state = await manager.create();
        }
        const onboardingComplete = state.currentStep === OnboardingStep.PROJECT_CREATION;
        dispatch(setBorrowerOnboardingComplete(onboardingComplete));
        if (onboardingComplete) {
          navigate(RouteDictionary.BORROWER_PROJECTS);
        } else {
          navigate(RouteDictionary.IDENTIFICATION_BORROWER);
        }
      } catch {
        dispatch(setBorrowerOnboardingComplete(false));
        navigate(RouteDictionary.IDENTIFICATION_BORROWER);
      }
      return;
    }

    navigate(RouteDictionary.MY_MONEY);
  };

  const loadProfile = async () => {
    setFailed(false);
    const manager = await createProfileManager();
    for (const delayMs of [0, ...PROFILE_RETRY_DELAYS]) {
      if (cancelledRef.current) {
        return;
      }
      if (delayMs > 0) {
        await delay(delayMs);
      }
      try {
        const profile = await manager.get();
        if (cancelledRef.current) {
          return;
        }
        await dispatch(set(profile));
        await redirectByProfile(profile);
        return;
      } catch {
        continue;
      }
    }
    if (!cancelledRef.current) {
      setFailed(true);
    }
  };

  useEffect(() => {
    cancelledRef.current = false;
    (async () => {
      const tokenExists = await createAuthTokenManager().has();
      if (!tokenExists) {
        navigate(RouteDictionary.ENTER);
        return;
      }
      if (profileData.uuid) {
        await redirectByProfile(profileData);
        return;
      }
      await loadProfile();
    })();
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  if (failed) {
    return (
      <div className="redirect-after-auth">
        <div>Не удалось загрузить профиль. Пожалуйста, попробуйте ещё раз.</div>
        <PrimaryButton color={PrimaryButtonColor.GREEN}>
          <Button onClick={loadProfile}>
            Повторить
          </Button>
        </PrimaryButton>
      </div>
    );
  }

  return (
    <div className="redirect-after-auth">
      Перенаправляем вас, пожалуйста, подождите...
    </div>
  );
};

export default RedirectAfterAuth;
